import React, { useState, useEffect } from 'react'
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  useTheme,
  Chip,
  CircularProgress
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { format } from 'date-fns'
import moment from 'moment'
import { getProjectIssueLog } from '../../../api/project/IssueLogs'

// Styled components for the timeline
const TimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1, 0),
  marginTop: theme.spacing(2),
  marginLeft: theme.spacing(1),
}))

const TimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 6,
  top: 0,
  bottom: 0,
  width: 2,
  height: '100%',
  backgroundColor: theme.palette.divider,
  zIndex: 0,
}))

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  marginBottom: theme.spacing(3),
}))

const TimelineDot = styled(Box)(({ theme, active }) => ({
  position: 'absolute',
  left: 0,
  top: 24,
  width: 14,
  height: 14,
  borderRadius: '50%',
  backgroundColor: active ? theme.palette.success.main : theme.palette.grey[400],
  border: `2px solid ${theme.palette.background.paper}`,
  zIndex: 1,
}))

const TimelineContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  flex: 1,
  marginLeft: theme.spacing(3),
  maxWidth: 'calc(100% - 30px)',
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}))

// Helper function to properly capitalize field names
const formatFieldName = (field) => {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper function to safely format values (especially objects) for display
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  
  // Handle date fields with consistent formatting
  if (typeof value === 'string' && (value.includes('-') || value.includes('/'))) {
    try {
      const momentDate = moment(value);
      if (momentDate.isValid()) {
        return momentDate.format('YYYY-MM-DD');
      }
    } catch (error) {
      // If date parsing fails, continue with normal formatting
    }
  }
  
  // Handle objects with name property (like status and assigned_to)
  if (typeof value === 'object' && value !== null) {
    if (value.name) {
      return value.name;
    }
    
    // Try to stringify non-null objects if they don't have a name
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '[Complex Object]';
    }
  }
  
  // For primitive values, convert to string
  return String(value);
}

// Helper function to get displayable value for comparison
const getDisplayValue = (obj, key) => {
  const value = obj[key];
  return formatValue(value);
}

// Helper function to normalize date strings for comparison
const normalizeDateString = (dateStr) => {
  try {
    if (!dateStr) return '';
    // Remove any non-numeric characters and get just YYYYMMDD
    return dateStr.replace(/[^0-9]/g, '').substring(0, 8);
  } catch (error) {
    return dateStr;
  }
}

const IssueLogs = ({open, setOpen, projectId}) => {
    const [issueLogs, setIssueLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const theme = useTheme();

    useEffect(() => {
        if (open && projectId) {
            getIssueLogs();
        }
    }, [open, projectId]);

    const getIssueLogs = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await getProjectIssueLog(projectId);
            
            // Ensure we're setting an array to the state
            if (res.data && Array.isArray(res.data)) {
                setIssueLogs(res.data);
            } else if (res.data) {
                // If res.data exists but is not an array (maybe an object with data property)
                console.log('API response format:', res.data);
                // Try to find the array in the response
                if (res.data.data && Array.isArray(res.data.data)) {
                    setIssueLogs(res.data.data);
                } else {
                    // If we can't find an array, set an empty array
                    console.warn('Issue logs data is not in expected array format', res.data);
                    setIssueLogs([]);
                }
            } else {
                // If no data at all, set empty array
                setIssueLogs([]);
            }
        } catch (err) {
            console.error('Error fetching issue logs:', err);
            setError('Failed to load issue logs. Please try again.');
            setIssueLogs([]);
        } finally {
            setLoading(false);
        }
    }

    // Helper function to format timestamp
    const formatDate = (timestamp) => {
      try {
        const date = new Date(timestamp);
        return format(date, 'dd MMM yyyy');
      } catch (error) {
        return timestamp;
      }
    }

    // Helper function to format time
    const formatTime = (timestamp) => {
      try {
        const date = new Date(timestamp);
        return format(date, 'hh:mm a');
      } catch (error) {
        return '';
      }
    }

    // Helper function to find what changed between old and new data
    const getChanges = (oldData, newData, actionType) => {
      if (actionType === 'create') {
        return 'Issue created';
      }
      
      const changes = [];
      if (oldData && newData) {
        Object.keys(newData).forEach(key => {
          // Skip null values or undefined old values
          if (newData[key] === null || oldData[key] === undefined) {
            return;
          }
          
          // Get formatted display values for comparison
          const oldDisplayValue = getDisplayValue(oldData, key);
          const newDisplayValue = getDisplayValue(newData, key);
          
          // For dates, compare normalized values
          if (key === 'start_date' || key === 'end_date') {
            const normalizedOld = normalizeDateString(oldDisplayValue);
            const normalizedNew = normalizeDateString(newDisplayValue);
            if (normalizedOld === normalizedNew) {
              return;
            }
          }
          
          // Only add to changes if the display values differ
          if (oldDisplayValue !== newDisplayValue) {
            changes.push({
              field: key,
              from: oldDisplayValue,
              to: newDisplayValue
            });
          }
        });
      }
      return changes;
    }

    // Helper to get status label
    const getActionStatusLabel = (log) => {
      switch (log.action_type) {
        case 'create':
          return 'Issue Created';
        case 'update':
          return 'Issue Updated';
        default:
          return log.action_type;
      }
    }

    // Helper to get a readable location string
    const getLocationString = (log) => {
      if (log.action_type === 'create') {
        return `Created by ${log.user_details.first_name} ${log.user_details.last_name}`;
      } else {
        return `Updated By: ${log.user_details.first_name} ${log.user_details.last_name}`;
      }
    }
    
    return (  
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { 
            minHeight: '60vh',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="medium">Issue Logs</Typography>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : Array.isArray(issueLogs) && issueLogs.length > 0 ? (
            <TimelineContainer>
              <TimelineLine />
              {issueLogs.map((log, index) => {
                const isFirst = index === 0;
                const changes = getChanges(log.old_data, log.new_data, log.action_type);
                
                return (
                  <TimelineItem key={log.id || index}>
                    <TimelineDot active={isFirst} />
                    
                    <TimelineContent sx={{ 
                      borderLeft: isFirst ? `3px solid ${theme.palette.success.main}` : 'none'
                    }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" color="primary" fontWeight="medium">
                              {getActionStatusLabel(log)}
                            </Typography>
                            <Chip 
                              label={log.action_type} 
                              size="small"
                              color={log.action_type === 'create' ? 'success' : 'primary'}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {getLocationString(log)}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body1" fontWeight="medium">
                            {formatDate(log.timestamp)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            At {formatTime(log.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      {log.action_type === 'update' && Array.isArray(changes) && changes.length > 0 && (
                        <Stack spacing={0.5} mt={1}>
                          <Typography variant="subtitle2">Changes:</Typography>
                          {changes
                            .filter(change => {
                              // Only show end_date if it's different from start_date
                              if (change.field === 'end_date') {
                                const startDateChange = changes.find(c => c.field === 'start_date');
                                return !startDateChange || startDateChange.to !== change.to;
                              }
                              return true;
                            })
                            .map((change, idx, filteredChanges) => (
                              <Box key={idx} sx={{ 
                                py: 0.5, 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: idx < filteredChanges.length - 1 ? `1px dashed ${theme.palette.divider}` : 'none'
                              }}>
                                <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '120px' }}>
                                  {formatFieldName(change.field)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" textAlign="right">
                                  {change.from} → {change.to}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      )}
                      
                      {log.action_type === 'create' && log.new_data && (
                        <Stack spacing={0.5} mt={1}>
                          <Typography variant="subtitle2">Initial Values:</Typography>
                          {Object.entries(log.new_data)
                            .filter(([key, value]) => {
                              // Only show end_date if it's different from start_date
                              if (key === 'end_date') {
                                return value !== null && value !== log.new_data.start_date;
                              }
                              return value !== null;
                            })
                            .map(([key, value], idx, filteredArr) => (
                              <Box key={key} sx={{ 
                                py: 0.5, 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: idx < filteredArr.length - 1 ? `1px dashed ${theme.palette.divider}` : 'none'
                              }}>
                                <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '120px' }}>
                                  {formatFieldName(key)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" textAlign="right">
                                  {formatValue(value)}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </TimelineContainer>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography color="text.secondary">No issue logs found</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    )
}

export default IssueLogs